// @ts-nocheck
import type { InterventionProcedure, ProcedureStep, PendingValidations, PendingWorkflow, PendingLog } from './types';
import { state, api, $, $$, toast, escHtml, truncate, todayStr, localDateKey, _timeAgo, _ensureMachineCache, _allMachinesCache, _populateLineSelect, formatDateTimeRO } from './core';
import { askText, askConfirm } from './dialogs';

/* ═══ PROCEDURE EDITOR (Inginer/Master) ══════════════════════════════ */

let _procMachineFilter = '';
let _procStatusFilter = '';

/* Filtrele stau DEASUPRA editorului și rămân apăsabile cât timp scrii pașii, iar
   handlerele lor reconstruiesc lista peste tot ce ai scris. Pe telefon, unde
   selectul e la două degete de câmpuri, o atingere greșită ștergea o procedură
   întreagă fără nicio întrebare. `_editorModificat` ne spune dacă avem ce pierde
   — dacă n-ai schimbat nimic, filtrul merge fără să te oprească din drum. */
let _editorModificat = false;

function _editorDeschis(): boolean {
    return !!document.querySelector('.proc-editor');
}

async function _potParasiEditorul(): Promise<boolean> {
    if (!_editorDeschis() || !_editorModificat) return true;
    return askConfirm('Ai modificări nesalvate',
        'Procedura are schimbări care nu au fost salvate. Dacă pleci acum, se pierd.',
        'Renunț la modificări');
}

/** Un select de filtrare care întreabă înainte să arunce editorul deschis. */
function _pazesteFiltrul(sel: HTMLSelectElement, aplica: (v: string) => void): void {
    let ultima = sel.value;
    sel.addEventListener('change', async () => {
        if (!await _potParasiEditorul()) {
            sel.value = ultima;         // punem selectul la loc: n-am schimbat nimic
            return;
        }
        ultima = sel.value;
        aplica(sel.value);
        _renderProceduresList();
    });
}

/** Căutarea din antet: ascunde cardurile care nu conțin textul (și titlurile de utilaj rămase goale). */
function _filtreazaCarduri(): void {
    const q = ((document.getElementById('proc-cauta') as HTMLInputElement)?.value || '').trim().toLowerCase();
    const vizibile = new Set<string>();
    document.querySelectorAll('#proc-list-container .proc-card').forEach(c => {
        const ok = !q || String((c as HTMLElement).dataset.cauta || '').includes(q);
        (c as HTMLElement).hidden = !ok;
        if (ok) vizibile.add(String((c as HTMLElement).dataset.grup || ''));
    });
    document.querySelectorAll('#proc-list-container .proc-grup-utilaj').forEach(g => {
        (g as HTMLElement).hidden = !vizibile.has(String((g as HTMLElement).dataset.grup || ''));
    });
}

export async function loadProceduresEditor(): Promise<void> {
    const sec = document.getElementById('sec-procedures');
    if (!sec) return;
    sec.innerHTML = `
        <div class="section-header"><h2>Fișe de Lucru — reglaje, calibrări și setări</h2></div>
        <p class="proc-intro">Cum se reglează sau se calibrează o componentă după înlocuire, ce parametri se pun pe un invertor nou,
            reglajele utilajului și ale matriței. Scrise din manualul fiecărui utilaj și verificate pas cu pas.
            Lucrările periodice (curățare, ungere, verificări) sunt în planul de mentenanță, nu aici.</p>
        <div class="proc-editor-filters" style="display:flex;gap:8px;margin-bottom:12px;flex-wrap:wrap">
            <select id="proc-machine-filter" class="form-select form-select-sm" style="max-width:200px">
                <option value="">Toate utilajele</option>
            </select>
            <select id="proc-status-filter" class="form-select form-select-sm" style="max-width:160px">
                <option value="">Toate statusurile</option>
                <option value="active">Active</option>
                <option value="draft">De verificat / draft</option>
                <option value="archived">Arhivate</option>
            </select>
            <input type="search" id="proc-cauta" class="form-select form-select-sm" placeholder="Caută: senzor, invertor, curea…" style="max-width:240px;min-height:36px">
            <button class="btn btn-sm btn-primary" id="proc-noua-btn" type="button">+ Fișă nouă de reglaj</button>
        </div>
        <div id="proc-list-container"><div class="alarm-empty"><span class="icon">⏳</span> Se încarcă...</div></div>
    `;

    await _ensureMachineCache();
    const machineSel = document.getElementById('proc-machine-filter') as HTMLSelectElement;
    if (machineSel) {
        (_allMachinesCache || []).forEach(m => {
            machineSel.innerHTML += `<option value="${escHtml(m.id)}">${escHtml(m.name || m.id)}</option>`;
        });
        // filtrul ales rămâne între vizite — selectul trebuie să-l și ARATE (30 sep 2026:
        // arăta „Toate utilajele" peste o listă filtrată pe un singur utilaj)
        machineSel.value = _procMachineFilter;
        if (machineSel.value !== _procMachineFilter) _procMachineFilter = '';
        _pazesteFiltrul(machineSel, v => { _procMachineFilter = v; });
    }
    const statusSel = document.getElementById('proc-status-filter') as HTMLSelectElement;
    if (statusSel) {
        statusSel.value = _procStatusFilter;
        if (statusSel.value !== _procStatusFilter) _procStatusFilter = '';
        _pazesteFiltrul(statusSel, v => { _procStatusFilter = v; });
    }
    document.getElementById('proc-cauta')?.addEventListener('input', () => _filtreazaCarduri());
    document.getElementById('proc-noua-btn')?.addEventListener('click', async () => {
        if (!await _potParasiEditorul()) return;
        const mid = (document.getElementById('proc-machine-filter') as HTMLSelectElement)?.value || '';
        if (!mid) { toast('Alege întâi utilajul din listă, apoi „Fișă nouă de reglaj”.', 'warning'); return; }
        const titlu = await askText('Fișă nouă de reglaj', { label: 'Ce se reglează? (ex. „Calibrarea senzorului de nivel după înlocuire”)', required: true, okText: 'Creează' });
        if (!titlu) return;
        try {
            const r = await api('/api/procedures', { method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ machine_id: mid, title: titlu, fel: 'reglaj',
                    steps: [{ instruction: 'Pasul 1 — de completat din manual (cu pagina).' }] }) });
            _openProcedureEditor(r.id);
        } catch (e: any) { toast('Nu s-a creat: ' + (e?.message || e), 'error'); }
    });
    await _renderProceduresList();
}

async function _renderProceduresList(): Promise<void> {
    const container = document.getElementById('proc-list-container');
    if (!container) return;
    _editorModificat = false;       // editorul dispare aici; nu mai avem ce păzi
    container.innerHTML = '<div class="alarm-empty"><span class="icon">⏳</span> Se încarcă...</div>';
    try {
        let url = '/api/procedures?';
        if (_procMachineFilter) url += 'machine_id=' + encodeURIComponent(_procMachineFilter) + '&';
        if (_procStatusFilter) url += 'status=' + encodeURIComponent(_procStatusFilter);
        const resp = await fetch(url);
        const procs: InterventionProcedure[] = await resp.json();
        // Totalul real vine în antet (19 aug 2026): înainte lista tăia tăcut la 200
        // și arăta ca și cum ar fi fost toate — 107 fișe active erau invizibile.
        const totalReal = parseInt(resp.headers.get('X-Total-Count') || '0', 10);
        if (procs.length === 0) {
            container.innerHTML = '<div class="alarm-empty"><span class="icon">📋</span> Nu există proceduri. Folosiți "Intervale fără procedură" pentru a genera.</div>';
            return;
        }
        let html = '';
        if (totalReal && totalReal > procs.length) {
            html += `<div class="alarm-empty" style="text-align:left;padding:8px 12px">
                ⚠️ Afișate <b>${procs.length}</b> din <b>${totalReal}</b> fișe.
                Restrânge după utilaj sau stare ca să le vezi pe celelalte.</div>`;
        }
        // grupate pe utilaj (30 sep 2026): fișele de reglaj se caută după utilaj și componentă
        const FEL: Record<string, string> = { reglaj: 'Reglaj', calibrare: 'Calibrare', setare_parametri: 'Setare parametri',
            inlocuire_cu_reglaj: 'Înlocuire + reglaj', punere_in_functiune: 'Punere în funcțiune' };
        const sortate = procs.slice().sort((a: any, b: any) =>
            String(a.machine_name || a.machine_id).localeCompare(String(b.machine_name || b.machine_id), 'ro')
            || String(a.title || '').localeCompare(String(b.title || ''), 'ro'));
        let utilajCurent = '';
        html += '<div class="proc-cards-grid">';
        sortate.forEach((p: any) => {
            const ut = p.machine_name || p.machine_id || '';
            if (ut !== utilajCurent) {
                utilajCurent = ut;
                html += `<div class="proc-grup-utilaj" data-grup="${escHtml(ut)}">${escHtml(ut)}${p.machine_name && p.machine_name !== p.machine_id ? ` <small>${escHtml(p.machine_id)}</small>` : ''}</div>`;
            }
            const deVerificat = p.status === 'draft' && (p.note_inginer || '').trim();
            const statusBadge = p.status === 'active' ? '<span class="badge badge-green">Activă</span>' :
                p.status === 'draft' ? `<span class="badge badge-yellow">${deVerificat ? 'De verificat la mașină' : 'Draft'}</span>` :
                '<span class="badge badge-gray">Arhivată</span>';
            const aiIcon = p.ai_generated ? ' <span title="Generată de AI">🤖</span>' : '';
            const cauta = [p.title, p.componenta, p.cand, ut, p.machine_id, FEL[p.fel] || ''].join(' ').toLowerCase();
            html += `<div class="proc-card" data-proc-id="${p.id}" data-cauta="${escHtml(cauta)}" data-grup="${escHtml(ut)}">
                <div class="proc-card-header">
                    <strong>${escHtml(p.title || p.action_ro || 'Procedură')}</strong>
                    ${statusBadge}${aiIcon}
                </div>
                <div class="proc-card-meta">
                    ${p.fel ? `<span class="proc-fel">${escHtml(FEL[p.fel] || p.fel)}</span> · ` : ''}${p.step_count || 0} pași
                    ${p.estimated_minutes ? ' · ~' + p.estimated_minutes + ' min' : ''}
                    ${p.componenta ? `<div class="proc-card-comp">${escHtml(truncate(p.componenta, 140))}</div>` : ''}
                    ${p.cand ? `<div class="proc-card-cand"><b>Când:</b> ${escHtml(truncate(p.cand, 160))}</div>` : ''}
                </div>
                <div class="proc-card-actions">
                    <button class="btn btn-xs btn-outline proc-preview-btn" data-proc-id="${p.id}">👁 Vizualizare</button>
                    <button class="btn btn-xs btn-outline proc-edit-btn" data-proc-id="${p.id}">✏ Editează</button>
                    ${p.status === 'draft' ? `<button class="btn btn-xs btn-green proc-activate-btn" data-proc-id="${p.id}">✓ Activează</button>` : ''}
                </div>
            </div>`;
        });
        html += '</div>';
        container.innerHTML = html;
        _filtreazaCarduri();
        container.querySelectorAll('.proc-edit-btn').forEach(btn => {
            (btn as HTMLElement).addEventListener('click', () => _openProcedureEditor(parseInt((btn as HTMLElement).dataset.procId || '0')));
        });
        container.querySelectorAll('.proc-preview-btn').forEach(btn => {
            (btn as HTMLElement).addEventListener('click', () => _previewProcedureById(parseInt((btn as HTMLElement).dataset.procId || '0')));
        });
        container.querySelectorAll('.proc-activate-btn').forEach(btn => {
            (btn as HTMLElement).addEventListener('click', async () => {
                const pid = parseInt((btn as HTMLElement).dataset.procId || '0');
                if (!await askConfirm('Activezi această procedură? Va fi folosită de operatori.')) return;
                const r = await fetch('/api/procedures/' + pid + '/activate', { method: 'POST' });
                const d = await r.json();
                if (d.ok) { toast('Procedură activată!', 'success'); _renderProceduresList(); }
                else toast(d.error || 'Eroare', 'error');
            });
        });
    } catch (e) {
        container.innerHTML = '<div class="alarm-empty"><span class="icon">❌</span> Eroare la încărcare</div>';
    }
}

async function _showMissingProcedures(): Promise<void> {
    const container = document.getElementById('proc-list-container');
    if (!container) return;
    container.innerHTML = '<div class="alarm-empty"><span class="icon">⏳</span> Se caută intervale fără procedură...</div>';
    try {
        let url = '/api/intervals-without-procedures';
        if (_procMachineFilter) url += '?machine_id=' + encodeURIComponent(_procMachineFilter);
        const resp = await fetch(url);
        const intervals = await resp.json();
        if (intervals.length === 0) {
            container.innerHTML = '<div class="alarm-empty"><span class="icon">✅</span> Toate intervalele au proceduri active!</div>';
            return;
        }
        /* Fabrica are peste o mie de intervale fără procedură. Randate deodată,
           umpleau pagina cu 1174 de carduri — pe telefonul mecanicului asta e
           blocare curată, iar utilitatea e zero: nimeni nu derulează o mie de
           carduri. Arătăm primele 100 și restul la cerere, cu numărul total
           spus din capul locului. Butonul de întoarcere lipsea cu totul: intrai
           în vederea asta și nu mai aveai cum ieși înapoi la lista de proceduri. */
        const PE_PAGINA = 100;
        let aratate = Math.min(PE_PAGINA, intervals.length);
        const capAtinsInitial = intervals.length;

        const deseneaza = () => {
        let html = '<div class="proc-missing-header" style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;margin-bottom:8px;font-weight:600">'
            + '<button class="btn btn-sm btn-outline" id="proc-missing-back">← Toate procedurile</button>'
            + `<span>${capAtinsInitial} intervale fără procedură activă`
            + (aratate < capAtinsInitial ? ` (afișate primele ${aratate})` : '') + '</span></div>';
        html += '<div class="proc-cards-grid">';
        intervals.slice(0, aratate).forEach(iv => {
            html += `<div class="proc-card proc-card-missing">
                <div class="proc-card-header">
                    <strong>${escHtml(iv.action_ro || '—')}</strong>
                    <span class="badge badge-red">Lipsă</span>
                </div>
                <div class="proc-card-meta">
                    ${escHtml(iv.machine_name || iv.machine_id)} · ${iv.interval_text || ''} · ${iv.interval_hours || 0}h
                </div>
                <div class="proc-card-actions">
                    <button class="btn btn-xs btn-primary proc-gen-ai-btn" data-iv-id="${iv.id}">🤖 Generează cu AI</button>
                    <button class="btn btn-xs btn-outline proc-create-manual-btn" data-iv-id="${iv.id}" data-machine-id="${iv.machine_id}" data-title="${escHtml(iv.action_ro || '')}">✏ Creează manual</button>
                </div>
            </div>`;
        });
        html += '</div>';
        if (aratate < capAtinsInitial) {
            html += `<div style="margin-top:10px;text-align:center"><button class="btn btn-sm btn-outline" id="proc-missing-more">Arată încă ${Math.min(PE_PAGINA, capAtinsInitial - aratate)} (au mai rămas ${capAtinsInitial - aratate})</button></div>`;
        }
        container.innerHTML = html;
        document.getElementById('proc-missing-back')?.addEventListener('click', () => _renderProceduresList());
        document.getElementById('proc-missing-more')?.addEventListener('click', () => {
            aratate = Math.min(aratate + PE_PAGINA, capAtinsInitial);
            deseneaza();
        });
        container.querySelectorAll('.proc-gen-ai-btn').forEach(btn => {
            (btn as HTMLElement).addEventListener('click', async () => {
                const ivId = parseInt((btn as HTMLElement).dataset.ivId || '0');
                (btn as HTMLElement).textContent = '⏳ Se generează...';
                (btn as HTMLElement).setAttribute('disabled', 'true');
                try {
                    const r = await fetch('/api/procedures/generate-from-ai', {
                        method: 'POST', headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ interval_id: ivId })
                    });
                    const d = await r.json();
                    if (d.id) {
                        toast('Procedură generată (' + (d.step_count || 0) + ' pași). Editează și activează.', 'success');
                        _openProcedureEditor(d.id);
                    } else {
                        toast(d.error || 'Eroare la generare', 'error');
                        (btn as HTMLElement).textContent = '🤖 Generează cu AI';
                        (btn as HTMLElement).removeAttribute('disabled');
                    }
                } catch (e) {
                    toast('Eroare rețea', 'error');
                    (btn as HTMLElement).textContent = '🤖 Generează cu AI';
                    (btn as HTMLElement).removeAttribute('disabled');
                }
            });
        });
        container.querySelectorAll('.proc-create-manual-btn').forEach(btn => {
            (btn as HTMLElement).addEventListener('click', async () => {
                const ivId = parseInt((btn as HTMLElement).dataset.ivId || '0');
                const machineId = (btn as HTMLElement).dataset.machineId || '';
                const title = (btn as HTMLElement).dataset.title || '';
                try {
                    const r = await fetch('/api/procedures', {
                        method: 'POST', headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ interval_id: ivId, machine_id: machineId, title: title, steps: [{ instruction: 'Pas 1 — descrieți instrucțiunea' }] })
                    });
                    const d = await r.json();
                    if (d.id) {
                        toast('Procedură creată. Adăugați pașii.', 'success');
                        _openProcedureEditor(d.id);
                    } else {
                        toast(d.error || 'Eroare', 'error');
                    }
                } catch (e) { toast('Eroare rețea', 'error'); }
            });
        });
        };      // deseneaza
        deseneaza();
    } catch (e) {
        container.innerHTML = '<div class="alarm-empty"><span class="icon">❌</span> Eroare la încărcare</div>';
    }
}

async function _openProcedureEditor(procId: number): Promise<void> {
    const container = document.getElementById('proc-list-container');
    if (!container) return;
    container.innerHTML = '<div class="alarm-empty"><span class="icon">⏳</span> Se încarcă procedura...</div>';
    try {
        const resp = await fetch('/api/procedures/' + procId);
        const proc: InterventionProcedure = await resp.json();
        if (!proc || !proc.id) { toast('Procedură negăsită', 'error'); _renderProceduresList(); return; }

        const eSchita = proc.status === 'draft' && proc.ai_generated;
        let html = `<div class="proc-editor">
            <div class="proc-editor-toolbar">
                <h3 style="margin:0;flex:1;min-width:200px">✏ ${escHtml(proc.title || '')}</h3>
                <button class="btn btn-sm btn-outline" id="proc-preview-live-btn">👁 Vizualizare</button>
                <button class="btn btn-sm btn-outline" id="proc-back-btn">← Înapoi</button>
            </div>
            ${eSchita ? `<div class="proc-schita-banner">🤖 Schiță generată automat — pașii nu provin dintr-un manual.
                Verificați fiecare pas, corectați, apoi «Salvează și Activează».</div>` : ''}
            <div class="proc-editor-meta" style="display:grid;grid-template-columns:2fr 1fr;gap:8px;margin-bottom:12px">
                <div><label>Titlu (ce vede mecanicul):</label><input type="text" id="proc-edit-title" class="form-control" value="${escHtml(proc.title || '')}"></div>
                <div><label>Timp estimat (min):</label><input type="number" id="proc-edit-time" class="form-control" value="${proc.estimated_minutes || ''}"></div>
                <div><label>Componenta (ce se reglează):</label><input type="text" id="proc-edit-componenta" class="form-control" value="${escHtml(proc.componenta || '')}"></div>
                <div><label>Când se face (ex. după înlocuirea senzorului, la schimbarea formatului):</label><input type="text" id="proc-edit-cand" class="form-control" value="${escHtml(proc.cand || '')}"></div>
                <div><label>Scule necesare (toată lucrarea):</label><input type="text" id="proc-edit-tools" class="form-control" value="${escHtml(proc.tools_required || '')}"></div>
                <div><label>⚠ Note de siguranță (afișate roșu, înaintea pașilor):</label><textarea id="proc-edit-safety" class="form-control" rows="2">${escHtml(proc.safety_notes || '')}</textarea></div>
            </div>
            <h4 style="margin:8px 0 6px">Pași — ordinea în care îi execută mecanicul</h4>
            <div id="proc-steps-editor">`;

        (proc.steps || []).forEach((s, i) => {
            html += _renderStepEditor(s, i);
        });

        html += `</div>
            <div style="margin-top:8px;display:flex;gap:8px">
                <button class="btn btn-sm btn-outline" id="proc-add-step-btn">+ Adaugă pas</button>
            </div>
            <div class="proc-editor-savebar">
                <button class="btn btn-sm btn-outline" id="proc-preview-live-btn2">👁 Vizualizare</button>
                <span style="flex:1"></span>
                <button class="btn btn-sm btn-primary" id="proc-save-btn">💾 Salvează</button>
                ${proc.status === 'draft' ? '<button class="btn btn-sm btn-green" id="proc-save-activate-btn">✓ Salvează și Activează</button>' : ''}
            </div>
        </div>`;

        container.innerHTML = html;
        _editorModificat = false;
        container.querySelector('.proc-editor')?.addEventListener('input', () => { _editorModificat = true; });

        document.getElementById('proc-back-btn')?.addEventListener('click', async () => {
            if (await _potParasiEditorul()) _renderProceduresList();
        });
        const previewLive = () => {
            const s = _collectEditorState();
            _showProcedurePreview({ ...proc, title: s.title, estimated_minutes: s.estimated_minutes,
                tools_required: s.tools_required, safety_notes: s.safety_notes }, s.steps as any);
        };
        document.getElementById('proc-preview-live-btn')?.addEventListener('click', previewLive);
        document.getElementById('proc-preview-live-btn2')?.addEventListener('click', previewLive);
        document.getElementById('proc-add-step-btn')?.addEventListener('click', () => {
            const stepsContainer = document.getElementById('proc-steps-editor');
            if (!stepsContainer) return;
            const idx = stepsContainer.querySelectorAll('.proc-step-edit-row').length;
            const div = document.createElement('div');
            div.innerHTML = _renderStepEditor({ step_order: idx + 1, instruction: '' }, idx, true);
            stepsContainer.appendChild(div.firstElementChild!);
            _bindStepRemoveButtons();
            _renumberSteps();
            (stepsContainer.lastElementChild?.querySelector('.proc-step-instr') as HTMLTextAreaElement)?.focus();
        });
        document.getElementById('proc-save-btn')?.addEventListener('click', () => _saveProcedure(procId, false));
        document.getElementById('proc-save-activate-btn')?.addEventListener('click', () => _saveProcedure(procId, true));
        _bindStepRemoveButtons();
    } catch (e) {
        container.innerHTML = '<div class="alarm-empty"><span class="icon">❌</span> Eroare</div>';
    }
}

/** Rezumatul lizibil al detaliilor unui pas, pentru rândul pliat. */
function _rezumatDetaliiPas(step: ProcedureStep): string {
    const parti: string[] = [];
    if (step.tools) parti.push('🔧 ' + truncate(step.tools, 30));
    if (step.parts_needed) parti.push('🧩 ' + truncate(step.parts_needed, 40));
    if (step.estimated_minutes) parti.push('⏱ ' + step.estimated_minutes + ' min');
    if (step.document_id) parti.push('📄 manual p.' + (step.document_page || '?'));
    if (step.notes) parti.push('📝 ' + truncate(step.notes, 25));
    return parti.length ? parti.join(' · ') : 'scule, piese, timp, sursă din manual…';
}

function _renderStepEditor(step: ProcedureStep, idx: number, deschis = false): string {
    /* `data-step-id` și `data-photo` sunt PURTĂTOARE, nu decor: salvarea
       reconstruiește pașii din DOM, iar serverul șterge și reinserează tot ce
       primește. Ce nu apare aici se pierde. Poza de referință, urcată separat
       prin .../steps/<id>/photo, nu era nici afișată, nici retrimisă — deci
       dispărea la primul „💾 Salvează", tăcut, iar fișierul rămânea orfan pe
       disc. Dacă mai adaugi câmpuri pe pas, treci-le și pe aici. */
    const poza = (step as any).photo_reference || '';
    const idPas = (step as any).id || '';
    /* Detaliile secundare stau pliate: în varianta veche fiecare pas afișa 8
       inputuri fără etichetă, iar valorile («112», «1») erau de nedescifrat —
       vezi captura lui Dan din 17 aug. Rezumatul de pe rândul pliat spune ce e
       înăuntru fără să deschizi. Avertismentul de siguranță rămâne mereu la
       vedere: e singurul câmp care nu are voie să fie ascuns. */
    return `<div class="proc-step-edit-row" data-step-id="${escHtml(String(idPas))}" data-photo="${escHtml(poza)}">
        <div class="proc-step-main">
            <span class="proc-step-num">${idx + 1}.</span>
            <textarea class="form-control proc-step-instr" rows="2"
                placeholder="Ce face mecanicul la acest pas — o acțiune clară, la imperativ">${escHtml(step.instruction || '')}</textarea>
            <div class="proc-step-btns">
                <button type="button" class="btn btn-xs btn-outline proc-step-up-btn" title="Mută mai sus">↑</button>
                <button type="button" class="btn btn-xs btn-outline proc-step-down-btn" title="Mută mai jos">↓</button>
                <button type="button" class="btn btn-xs btn-red proc-step-remove-btn" title="Șterge pas">✕</button>
            </div>
        </div>
        <div class="proc-step-safety-row">
            <label>⚠</label>
            <input type="text" class="form-control proc-step-safety-input"
                placeholder="Avertisment de siguranță pentru acest pas (mecanicul îl vede cu roșu) — gol dacă nu e cazul"
                value="${escHtml(step.safety_warning || '')}">
        </div>
        <details class="proc-step-details"${deschis ? ' open' : ''}>
            <summary><span class="proc-step-det-sum">${escHtml(_rezumatDetaliiPas(step))}</span></summary>
            <div class="proc-step-fields">
                <div class="proc-fld"><label>🔧 Scule pentru acest pas</label>
                    <input type="text" class="form-control proc-step-tools-input" value="${escHtml(step.tools || '')}"></div>
                <div class="proc-fld"><label>🧩 Piese necesare</label>
                    <input type="text" class="form-control proc-step-parts-input" value="${escHtml(step.parts_needed || '')}"></div>
                <div class="proc-fld proc-fld-mic"><label>⏱ Timp (min)</label>
                    <input type="number" class="form-control proc-step-time-input" value="${step.estimated_minutes || ''}"></div>
                <div class="proc-fld proc-fld-mic"><label>📄 Doc. (ID intern)</label>
                    <input type="number" class="form-control proc-step-docid-input" value="${step.document_id || ''}"></div>
                <div class="proc-fld proc-fld-mic"><label>Pagina din manual</label>
                    <input type="number" class="form-control proc-step-page-input" value="${step.document_page || ''}"></div>
                <div class="proc-fld"><label>Capitol / sursă</label>
                    <input type="text" class="form-control proc-step-chapter-input" value="${escHtml(step.chapter_title || '')}"></div>
                <div class="proc-fld proc-fld-lat"><label>📝 Observații inginer (nu se afișează mecanicului)</label>
                    <input type="text" class="form-control proc-step-notes-input" value="${escHtml(step.notes || '')}"></div>
            </div>
        </details>
        ${poza ? `<div class="proc-step-photo-edit">
            <img src="/uploads/${escHtml(poza)}" alt="Poză de referință" loading="lazy">
            <span>Poză de referință — se păstrează la salvare</span>
            <button type="button" class="btn btn-xs btn-outline proc-step-photo-del" title="Scoate poza">✕ Scoate poza</button>
        </div>` : ''}
    </div>`;
}

/* ─── Vizualizare: fișa exact cum o primește mecanicul ─────────────── */

function _collectEditorState(): { title: string; estimated_minutes: number | null;
        tools_required: string; safety_notes: string; steps: any[] } {
    const steps: any[] = [];
    document.querySelectorAll('#proc-steps-editor .proc-step-edit-row').forEach(row => {
        const r = row as HTMLElement;
        steps.push({
            id: parseInt(r.dataset.stepId || '0') || null,
            photo_reference: r.dataset.photo || '',
            instruction: (row.querySelector('.proc-step-instr') as HTMLTextAreaElement)?.value || '',
            tools: (row.querySelector('.proc-step-tools-input') as HTMLInputElement)?.value || '',
            parts_needed: (row.querySelector('.proc-step-parts-input') as HTMLInputElement)?.value || '',
            estimated_minutes: parseInt((row.querySelector('.proc-step-time-input') as HTMLInputElement)?.value || '0') || null,
            document_id: parseInt((row.querySelector('.proc-step-docid-input') as HTMLInputElement)?.value || '0') || null,
            document_page: parseInt((row.querySelector('.proc-step-page-input') as HTMLInputElement)?.value || '0') || null,
            chapter_title: (row.querySelector('.proc-step-chapter-input') as HTMLInputElement)?.value || '',
            safety_warning: (row.querySelector('.proc-step-safety-input') as HTMLInputElement)?.value || '',
            notes: (row.querySelector('.proc-step-notes-input') as HTMLInputElement)?.value || '',
        });
    });
    return {
        title: (document.getElementById('proc-edit-title') as HTMLInputElement)?.value || '',
        estimated_minutes: parseInt((document.getElementById('proc-edit-time') as HTMLInputElement)?.value || '0') || null,
        tools_required: (document.getElementById('proc-edit-tools') as HTMLInputElement)?.value || '',
        componenta: (document.getElementById('proc-edit-componenta') as HTMLInputElement)?.value || '',
        cand: (document.getElementById('proc-edit-cand') as HTMLInputElement)?.value || '',
        safety_notes: (document.getElementById('proc-edit-safety') as HTMLTextAreaElement)?.value || '',
        steps,
    };
}

async function _previewProcedureById(procId: number): Promise<void> {
    try {
        const resp = await fetch('/api/procedures/' + procId);
        const proc: InterventionProcedure = await resp.json();
        if (!proc || !proc.id) { toast('Procedură negăsită', 'error'); return; }
        _showProcedurePreview(proc, proc.steps || []);
    } catch (e) { toast('Eroare rețea', 'error'); }
}

/** Fișa randată ca pentru mecanic — aceleași informații, aceeași ordine,
    fără niciun câmp de editare. Se deschide peste orice ecran. */
/** Ce spune o fișă de reglaj pe lângă pași: componenta, când, varianta, ce e de verificat,
 *  valorile din datasheet-ul matriței și paginile din manual. */
function _blocuriReglaj(proc: any): string {
    const jsn = (t: any) => { try { return JSON.parse(t || '[]') || []; } catch (_e) { return []; } };
    const surse = jsn(proc.surse);
    const valori = jsn(proc.valori_datasheet);
    const b: string[] = [];
    if (proc.componenta) b.push(`<div class="proc-pv-bloc"><b>Componenta:</b> ${escHtml(proc.componenta)}</div>`);
    if (proc.cand) b.push(`<div class="proc-pv-bloc"><b>Când se face:</b> ${escHtml(proc.cand)}</div>`);
    if (proc.varianta) b.push(`<div class="proc-pv-bloc"><b>Varianta:</b> ${escHtml(proc.varianta)}</div>`);
    if (proc.note_inginer) {
        // pe o fișă „draft", ce e de confirmat la mașină stă în variantă și în note — se activează după
        b.push(`<div class="proc-pv-bloc${proc.status === 'draft' ? ' proc-pv-verif' : ''}"><b>${proc.status === 'draft'
            ? 'Note — de confirmat la mașină înainte de activare:' : 'Note pentru inginer:'}</b> ${escHtml(proc.note_inginer)}</div>`);
    }
    if (valori.length) {
        b.push(`<div class="proc-pv-bloc"><b>Valori din datasheet-ul matriței:</b><ul>${valori.map((v: any) =>
            `<li>${escHtml(typeof v === 'string' ? v : JSON.stringify(v))}</li>`).join('')}</ul></div>`);
    }
    if (surse.length) {
        b.push(`<div class="proc-pv-bloc"><b>Sursa:</b> ${surse.map((s: any) => {
            const p1 = parseInt(String(s.pagini || '').split(/[-–,]/)[0], 10);
            const t = `doc. ${s.document_id}${s.sectiune ? ', ' + escHtml(s.sectiune) : ''}, p. ${escHtml(String(s.pagini || ''))}`;
            return s.document_id && p1 ? `<a target="_blank" href="/api/documents/${s.document_id}/view?page=${p1}">${t}</a>` : t;
        }).join(' · ')}</div>`);
    }
    return b.join('');
}

function _showProcedurePreview(proc: any, steps: any[]): void {
    const statusEtich = proc.status === 'active' ? '<span class="badge badge-green">Activă — mecanicul o vede</span>'
        : proc.status === 'draft' ? '<span class="badge badge-yellow">Draft — mecanicul NU o vede încă</span>'
        : '<span class="badge badge-gray">Arhivată</span>';
    let pasiHtml = '';
    steps.forEach((s, i) => {
        const chips: string[] = [];
        if (s.tools) chips.push(`<span class="proc-pv-chip">🔧 ${escHtml(s.tools)}</span>`);
        if (s.parts_needed) chips.push(`<span class="proc-pv-chip proc-pv-chip-parts">🧩 ${escHtml(s.parts_needed)}</span>`);
        if (s.estimated_minutes) chips.push(`<span class="proc-pv-chip">⏱ ${s.estimated_minutes} min</span>`);
        if (s.notes && /^Valoare: /.test(String(s.notes))) chips.push(`<span class="proc-pv-chip proc-pv-chip-val">${escHtml(s.notes)}</span>`);
        if (s.document_id && s.document_page) {
            chips.push(`<a class="proc-pv-chip proc-pv-chip-doc" target="_blank"
                href="/api/documents/${s.document_id}/view?page=${s.document_page}">📄 manual, p.${s.document_page}</a>`);
        }
        pasiHtml += `<li class="proc-pv-step">
            <div class="proc-pv-instr">${escHtml(s.instruction || '')}</div>
            ${s.safety_warning ? `<div class="proc-pv-warn">⚠ ${escHtml(s.safety_warning)}</div>` : ''}
            ${chips.length ? `<div class="proc-pv-chips">${chips.join('')}</div>` : ''}
            ${s.photo_reference ? `<img class="proc-pv-photo" src="/uploads/${escHtml(s.photo_reference)}" loading="lazy"
                onclick="window.open(this.src,'_blank')">` : ''}
        </li>`;
    });
    const el = document.createElement('div');
    el.className = 'proc-preview-overlay';
    el.innerHTML = `<div class="proc-preview-card">
        <div class="proc-pv-header">
            <div style="flex:1;min-width:0">
                <h3>${escHtml(proc.title || 'Fișă de lucru')}</h3>
                <div class="proc-pv-meta">${escHtml(proc.machine_name || proc.machine_id || '')}
                    ${proc.estimated_minutes ? ' · ~' + proc.estimated_minutes + ' min' : ''} · ${steps.length} pași ${statusEtich}</div>
            </div>
            <button class="btn btn-sm btn-outline proc-pv-close">✕ Închide</button>
        </div>
        ${_blocuriReglaj(proc)}
        ${proc.safety_notes ? `<div class="proc-pv-safety">⚠ ${escHtml(proc.safety_notes)}</div>` : ''}
        ${proc.tools_required ? `<div class="proc-pv-tools">🔧 Scule necesare: ${escHtml(proc.tools_required)}</div>` : ''}
        <ol class="proc-pv-steps">${pasiHtml}</ol>
        <div class="proc-pv-footer">${proc.created_by === 'reglaje_din_manual_2026-09-30'
            ? 'Scrisă din manualul utilajului și verificată pas cu pas pe pagină (30 sep 2026).'
            : 'Fișă de lucru.'}</div>
    </div>`;
    document.body.appendChild(el);
    el.querySelector('.proc-pv-close')?.addEventListener('click', () => el.remove());
    el.addEventListener('click', e => { if (e.target === el) el.remove(); });
}

function _bindStepRemoveButtons(): void {
    document.querySelectorAll('.proc-step-remove-btn').forEach(btn => {
        btn.replaceWith(btn.cloneNode(true));
    });
    document.querySelectorAll('.proc-step-remove-btn').forEach(btn => {
        (btn as HTMLElement).addEventListener('click', () => {
            const row = (btn as HTMLElement).closest('.proc-step-edit-row');
            if (row) row.remove();
            _editorModificat = true;
            _renumberSteps();
        });
    });
    // Reordonare: mutatul unui pas e editare adevărată (ordinea E procedura),
    // deci aprinde și paznicul de modificări.
    const muta = (btn: Element, sus: boolean) => {
        const row = (btn as HTMLElement).closest('.proc-step-edit-row');
        if (!row) return;
        const vecin = sus ? row.previousElementSibling : row.nextElementSibling;
        if (!vecin || !vecin.classList.contains('proc-step-edit-row')) return;
        if (sus) vecin.before(row); else vecin.after(row);
        _editorModificat = true;
        _renumberSteps();
        (row as HTMLElement).scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    };
    document.querySelectorAll('.proc-step-up-btn, .proc-step-down-btn').forEach(btn => {
        btn.replaceWith(btn.cloneNode(true));
    });
    document.querySelectorAll('.proc-step-up-btn').forEach(btn => {
        (btn as HTMLElement).addEventListener('click', () => muta(btn, true));
    });
    document.querySelectorAll('.proc-step-down-btn').forEach(btn => {
        (btn as HTMLElement).addEventListener('click', () => muta(btn, false));
    });
    // Scoaterea pozei e o alegere explicită — golim purtătorul, nu ștergem fișierul.
    document.querySelectorAll('.proc-step-photo-del').forEach(btn => {
        btn.replaceWith(btn.cloneNode(true));
    });
    document.querySelectorAll('.proc-step-photo-del').forEach(btn => {
        (btn as HTMLElement).addEventListener('click', () => {
            const row = (btn as HTMLElement).closest('.proc-step-edit-row') as HTMLElement | null;
            if (!row) return;
            row.dataset.photo = '';
            row.querySelector('.proc-step-photo-edit')?.remove();
            toast('Poza se scoate la salvare', 'info');
        });
    });
}

function _renumberSteps(): void {
    const randuri = document.querySelectorAll('#proc-steps-editor .proc-step-edit-row');
    randuri.forEach((row, i) => {
        const num = row.querySelector('.proc-step-num');
        if (num) num.textContent = (i + 1) + '.';
    });
    /* Ștergi ultimul pas și rămâne un gol mut: containerul e acolo, dar fără
       înălțime, deci pare că s-a stricat ceva. Salvarea va fi oricum refuzată —
       o spunem din timp, nu abia după apăsarea butonului. */
    const cutie = document.getElementById('proc-steps-editor');
    if (!cutie) return;
    const gol = cutie.querySelector('.proc-steps-empty');
    if (randuri.length === 0 && !gol) {
        const d = document.createElement('div');
        d.className = 'alarm-empty proc-steps-empty';
        d.innerHTML = '<span class="icon">📝</span> Nicio instrucțiune. O procedură fără pași nu se poate salva — adaugă cel puțin unul.';
        cutie.appendChild(d);
    } else if (randuri.length > 0 && gol) {
        gol.remove();
    }
}

async function _saveProcedure(procId: number, activate: boolean): Promise<void> {
    const s = _collectEditorState();
    if (s.steps.length === 0) { toast('Adăugați cel puțin un pas', 'warning'); return; }

    try {
        const r = await fetch('/api/procedures/' + procId, {
            method: 'PUT', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ title: s.title, estimated_minutes: s.estimated_minutes,
                tools_required: s.tools_required, safety_notes: s.safety_notes, steps: s.steps })
        });
        const d = await r.json();
        if (!d.ok) { toast(d.error || 'Eroare', 'error'); return; }
        toast('Procedură salvată!', 'success');

        if (activate) {
            const ar = await fetch('/api/procedures/' + procId + '/activate', { method: 'POST' });
            const ad = await ar.json();
            if (ad.ok) toast('Procedură activată!', 'success');
            else toast(ad.error || 'Eroare activare', 'error');
        }
        _renderProceduresList();
    } catch (e) { toast('Eroare rețea', 'error'); }
}


/* ═══ VALIDATION INBOX (Inginer/Master) ══════════════════════════════ */

export async function loadValidationInbox(): Promise<void> {
    const sec = document.getElementById('sec-validation');
    if (!sec) return;
    sec.innerHTML = `
        <div class="section-header"><h2>De validat — Intervenții finalizate</h2></div>
        <div id="validation-tabs" style="display:flex;gap:8px;margin-bottom:12px;flex-wrap:wrap">
            <button class="btn btn-sm btn-outline val-tab-btn" data-tab="workflows">Workflow-uri</button>
            <button class="btn btn-sm btn-outline val-tab-btn" data-tab="logs">Intervenții și lucrări</button>
            <button class="btn btn-sm btn-outline val-tab-btn" data-tab="enrichments">Completări propuse</button>
            <button class="btn btn-sm btn-outline val-tab-btn" data-tab="briefing" title="Propunerile briefingului de dimineață — nu sunt lucrări de validat, dar așteaptă o decizie">Propuneri briefing</button>
            <button class="btn btn-sm btn-outline val-tab-btn" data-tab="coleg" title="Ce a validat singur colegul AI, eșantionul de controlat și ce ai respins">Colegul AI</button>
        </div>
        <div id="validation-container"><div class="alarm-empty"><span class="icon">⏳</span> Se încarcă...</div></div>
    `;
    sec.querySelectorAll('.val-tab-btn').forEach(btn => {
        (btn as HTMLElement).addEventListener('click', () => {
            const tab = (btn as HTMLElement).dataset.tab || 'workflows';
            _valFilaAleasa = true;
            _marcheazaFilaValidare(tab);
            _renderValidationTab(tab);
        });
    });
    /* Fila rămâne cea de la vizita trecută (`_valActiveTab` trăiește cât pagina),
       deci butonul evidențiat se pune DUPĂ ce știm care e — nu din HTML fix.
       Altfel intri a doua oară în secțiune și „Workflow-uri" e aprins în timp ce
       dedesubt se văd intervențiile urgente: validezi crezând că vezi altceva. */
    _marcheazaFilaValidare(_valActiveTab);
    await _fetchAndRenderValidations();
}

/** Aprinde butonul filei date și le stinge pe celelalte. */
function _marcheazaFilaValidare(tab: string): void {
    document.querySelectorAll('#validation-tabs .val-tab-btn').forEach(b => {
        const activ = (b as HTMLElement).dataset.tab === tab;
        b.classList.toggle('btn-primary', activ);
        b.classList.toggle('btn-outline', !activ);
    });
}

let _pendingData: PendingValidations | null = null;

/* ═══ COLEGUL AI (31 oct 2026, Dan; strategia v2 din 1 nov) — ce a validat singur, eșantionul, respingerile ═════
 * Regula: services/coleg_validare.py + citirea AI din services/coleg_citire.py. Rutina (ce trecea „auto”) o validează
 * fără istoric; lucrările de execuție, cu cel puțin 2 închideri anterioare validate de inginer; siguranța, rarele,
 * urgențele și ce e critic rămân la inginer. Mereu: dovadă, citirea AI „făcută”, fără semnale de grabă, fără
 * respingeri în 90 de zile. 1 din 10 validări ajunge aici ca eșantion de controlat. „Respinge” are aceleași efecte
 * ca în celelalte file și oprește validarea AI pe acea lucrare. */
interface ColegLucrare {
    log_id: number; machine_id: string; machine_name: string | null; action: string; technician: string;
    date: string; text: string; clasa: string; motiv_clasa: string; decizie: string; motive: string[];
    istoric: number[]; incredere: number | null; intrebare: string | null; raspuns: string | null; sarit: number;
    esantion: number; control: string | null; control_de: string | null; control_la: string | null;
    control_motiv: string | null; validation_notes: string | null; creat_la: string; actualizat_la: string | null;
    treapta: string | null; citire_stare: string | null; citire_motiv: string | null;
}
interface ColegRezumat {
    activ: boolean; esantion_la: number; la_inginer_azi: number;
    citire_activa: boolean; fara_citire_azi: number; in_citire: number; min_validari_executie: number;
    validate_azi: ColegLucrare[]; de_controlat: ColegLucrare[]; respinse: ColegLucrare[];
    intrebari_azi: { puse: number; raspunse: number; sarite: number };
}
let _colegData: ColegRezumat | null = null;

function _colegCard(c: ColegLucrare, butoane: boolean): string {
    const ist = c.istoric.length ? c.istoric.slice(0, 5).map(i => '#' + i).join(', ') : '—';
    return `<div class="val-card" data-coleg-log="${c.log_id}">
        <div class="val-card-header">
            <strong>${escHtml(truncate(c.action || 'Lucrare', 140))}</strong>
            <span class="val-card-date">${escHtml(formatDateTimeRO(c.actualizat_la || c.creat_la))}</span>
        </div>
        <div class="val-card-meta">${escHtml(c.machine_name || c.machine_id)} · ${escHtml(c.technician || '—')}`
            + (c.incredere != null ? ` · încredere ${Math.round(c.incredere * 100)}%` : '') + `</div>
        <div class="val-card-meta">A scris: ${c.text ? '„' + escHtml(truncate(c.text, 220)) + '”' : '<i>nimic</i>'}`
            + (c.intrebare ? `<br>Întrebat: „${escHtml(c.intrebare)}” → ${c.raspuns ? '„' + escHtml(c.raspuns) + '”' : '<i>a trecut peste</i>'}` : '')
            + `</div>
        <div class="val-card-meta" style="color:var(--text-muted)">${c.citire_stare === 'facuta'
            ? 'Citirea AI: ' + escHtml(c.citire_motiv || 'făcută')
            : 'Validată doar pe reguli — citirea AI n-a fost disponibilă'}${c.istoric.length
            ? ' · sprijinită pe închiderile validate de inginer: ' + ist : ''}</div>
        ${c.control === 'respins' ? `<div class="val-card-meta" style="color:var(--red)">Respins de ${escHtml(c.control_de || '')}: ${escHtml(c.control_motiv || '')}</div>` : ''}
        ${c.control === 'confirmat' ? `<div class="val-card-meta" style="color:var(--green)">Confirmat de ${escHtml(c.control_de || '')}</div>` : ''}
        ${butoane ? `<div class="val-card-actions" style="margin-top:8px;display:flex;gap:6px">
            ${c.control === 'de_controlat' ? `<button class="btn btn-xs btn-green coleg-ok" data-log-id="${c.log_id}">✓ E bine</button>` : ''}
            <button class="btn btn-xs btn-red coleg-respinge" data-log-id="${c.log_id}">✕ Respinge</button>
        </div>` : ''}
    </div>`;
}

function _renderColegTab(container: HTMLElement): void {
    const d = _colegData;
    if (!d) {
        container.innerHTML = '<div class="alarm-empty"><span class="icon">⏳</span> Colegul AI nu a răspuns — reîncarcă pagina.</div>';
        return;
    }
    const q = d.intrebari_azi;
    let html = `<div class="ss-sub" style="margin-bottom:10px">Azi: <b>${d.validate_azi.length}</b> validate de colegul AI · `
        + `<b>${d.la_inginer_azi}</b> trimise la tine · întrebări puse ${q.puse} (cu răspuns ${q.raspunse}, sărite ${q.sarite})`
        + (d.fara_citire_azi ? ` · <b>${d.fara_citire_azi}</b> validate doar pe reguli (fără citirea AI)` : '')
        + (d.in_citire ? ` · ${d.in_citire} în citire acum` : '') + `. `
        + `Validează singur rutina (curățări, ungeri, verificări, valori citite) fără istoric, iar lucrările de execuție `
        + `doar cu cel puțin ${d.min_validari_executie} închideri anterioare validate de tine. Mereu cere dovadă, citirea AI `
        + `„făcută”, nicio grabă și nicio respingere în 90 de zile; siguranța, lucrările rare, urgențele și ce e critic `
        + `vin la tine. 1 din ${d.esantion_la} validări ți-o arată mai jos, ca s-o controlezi.`
        + (d.activ ? '' : ' <b>Colegul e oprit (FABRICA_COLEG_AI=0).</b>')
        + (d.activ && !d.citire_activa ? ' <b>Citirea AI e oprită sau fără buget — validează doar regulile.</b>' : '')
        + `</div>`;
    html += `<h3 style="margin:12px 0 6px">De controlat (${d.de_controlat.length})</h3>`;
    html += d.de_controlat.length ? d.de_controlat.map(c => _colegCard(c, true)).join('')
        : '<div class="alarm-empty"><span class="icon">✅</span> Nimic de controlat.</div>';
    html += `<h3 style="margin:16px 0 6px">Validate de colegul AI azi (${d.validate_azi.length})</h3>`;
    html += d.validate_azi.length ? d.validate_azi.map(c => _colegCard(c, c.control !== 'respins')).join('')
        : '<div class="alarm-empty"><span class="icon">·</span> Nicio lucrare validată de colegul AI azi.</div>';
    if (d.respinse.length) {
        html += `<h3 style="margin:16px 0 6px">Respinse de tine după validarea AI — ultimele 30 de zile (${d.respinse.length})</h3>`;
        html += d.respinse.map(c => _colegCard(c, false)).join('');
    }
    container.innerHTML = html;
    container.querySelectorAll('.coleg-ok').forEach(btn => {
        (btn as HTMLElement).addEventListener('click', () => _colegControl(parseInt((btn as HTMLElement).dataset.logId || '0'), 'confirmat'));
    });
    container.querySelectorAll('.coleg-respinge').forEach(btn => {
        (btn as HTMLElement).addEventListener('click', () => _colegControl(parseInt((btn as HTMLElement).dataset.logId || '0'), 'respins'));
    });
}

async function _colegControl(logId: number, decizie: 'confirmat' | 'respins'): Promise<void> {
    let motiv = '';
    if (decizie === 'respins') {
        const m = await askText('De ce o respingi?', {
            label: 'Lucrarea trece pe „respins”, iar colegul AI nu mai validează singur lucrarea asta până o validezi tu din nou.',
            multiline: true, required: true, okText: 'Respinge' });
        if (m === null) return;
        motiv = m;
    }
    try {
        await api(`/api/coleg/${logId}/control`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ decizie, motiv }) });
        toast(decizie === 'respins' ? 'Respinsă — colegul nu o mai validează singur' : 'Confirmată', 'success');
        await _fetchAndRenderValidations();
        updateValidationBadge();
    } catch (e: unknown) {
        toast('Eroare: ' + (e instanceof Error ? e.message : String(e)), 'error');
    }
}
let _valAgeFilter: 'toate' | 'd7' | 'd830' | 'gt30' = 'toate';
let _valActiveTab = 'workflows';
let _valFilaAleasa = false;   // omul a apăsat singur o filă — de acum o respectăm

function _valAgeDays(d: string | null | undefined): number {
    const t = new Date(String(d || '')).getTime();
    return isNaN(t) ? 9999 : Math.floor((Date.now() - t) / 86400000);
}

function _updateValTabCounters(): void {
    if (!_pendingData) return;
    // 31 oct 2026: numărul REAL — listele se opresc la 100 („100” pe fila cu 190 de workflow-uri)
    const counts: Record<string, number> = {
        workflows: _pendingData.workflows_total ?? (_pendingData.workflows || []).length,
        logs: _pendingData.logs_total ?? (_pendingData.logs || []).length,
        enrichments: ((_pendingData as any).enrichments || []).length,
        briefing: Number((_pendingData as any).briefing_total || 0),
    };
    const labels: Record<string, string> = {
        workflows: 'Workflow-uri', logs: 'Intervenții și lucrări', enrichments: 'Completări propuse',
        briefing: 'Propuneri briefing',
    };
    document.querySelectorAll('#validation-tabs .val-tab-btn').forEach(btn => {
        const tab = (btn as HTMLElement).dataset.tab || '';
        if (labels[tab]) btn.textContent = `${labels[tab]} (${counts[tab] || 0})`;
        // fila colegului arată doar câte așteaptă controlul tău — validările lui nu intră în insignă
        if (tab === 'coleg') btn.textContent = _colegData && _colegData.de_controlat.length
            ? `Colegul AI (${_colegData.de_controlat.length} de controlat)` : 'Colegul AI';
    });
}

async function _fetchAndRenderValidations(): Promise<void> {
    try {
        const [resp, rezColeg] = await Promise.all([
            fetch('/api/pending-validations'),
            fetch('/api/coleg/rezumat').then(r => (r.ok ? r.json() : null)).catch(() => null),
        ]);
        if (!resp.ok) throw new Error('HTTP ' + resp.status);
        _pendingData = await resp.json();
        _colegData = rezColeg;
        _updateValTabCounters();
        // 30 sep 2026: se intra mereu pe „Workflow-uri", chiar goală („Toate workflow-urile sunt
        // validate!"), în timp ce lucrările de validat stăteau în fila vecină. Cât omul n-a ales
        // singur o filă, se deschide prima care are ceva.
        if (!_valFilaAleasa && _pendingData) {
            const nr: Record<string, number> = {
                workflows: (_pendingData.workflows || []).length,
                logs: (_pendingData.logs || []).length,
                enrichments: ((_pendingData as any).enrichments || []).length,
                briefing: Number((_pendingData as any).briefing_total || 0),
            };
            if (!nr[_valActiveTab]) {
                const prima = ['workflows', 'logs', 'enrichments', 'briefing'].find(t => nr[t] > 0);
                if (prima) { _valActiveTab = prima; _marcheazaFilaValidare(prima); }
            }
        }
        _renderValidationTab(_valActiveTab);
    } catch (e) {
        const c = document.getElementById('validation-container');
        if (c) c.innerHTML = '<div class="alarm-empty"><span class="icon">❌</span> Eroare la încărcare</div>';
    }
}

function _renderValidationTab(tab: string): void {
    const container = document.getElementById('validation-container');
    if (!container || !_pendingData) return;
    _valActiveTab = tab;

    if (tab === 'coleg') {
        _renderColegTab(container);
        return;
    }

    if (tab === 'briefing') {
        /* Audit 20 sep 2026, §3.2: briefingul scrie workflow-uri `pending` fără
         * mașină; inbox-ul cere `completed` și nu le vedea. Din 29 sep se decid
         * AICI: Refuz (cu motiv) sau Fă din ea o lucrare (utilaj, om, dată).
         * „Aprobă"-ul de workflow nu merge pe ele — ar scrie o lucrare falsă în
         * jurnal. Briefingul citește decizia și o marchează la fel în Hub. */
        const items: any[] = (_pendingData as any).briefing || [];
        const total = Number((_pendingData as any).briefing_total || 0);
        if (!items.length) {
            container.innerHTML = '<div class="alarm-empty"><span class="icon">✅</span> Nicio propunere din briefing în așteptare.</div>';
            return;
        }
        let html = `<div class="ss-sub" style="margin-bottom:10px">${total} propuneri ale briefingului de dimineață, fără utilaj. `
            + `Nu sunt lucrări făcute — sunt planuri propuse. Refuză-le cu motiv sau fă din ele o lucrare; decizia ajunge și în Hub.</div>`;
        items.forEach(b => {
            const nota = String(b.notes || '');
            const m = nota.match(/^\[Auto-generat din briefing #(\d+)\]\s*(.*)$/s);
            const [actiune, motiv] = String(m ? m[2] : nota).split('|').map(x => (x || '').trim());
            html += `<div class="val-card" data-wf-id="${b.id}">
                <div class="val-card-header">
                    <strong>${escHtml(actiune || '—')}</strong>
                    <span class="val-card-date">${escHtml(formatDateTimeRO(b.started_at))}</span>
                </div>
                ${motiv ? `<div class="val-card-meta">${escHtml(truncate(motiv, 220))}</div>` : ''}
                <div class="val-card-meta">briefing #${escHtml(m ? m[1] : '?')} · propunerea #${b.id}</div>
                <div class="val-card-actions" style="margin-top:8px;display:flex;gap:6px;flex-wrap:wrap">
                    <button class="btn btn-xs btn-green brf-lucrare" data-wf-id="${b.id}">＋ Fă din ea o lucrare</button>
                    <button class="btn btn-xs btn-red brf-refuz" data-wf-id="${b.id}">✕ Refuz</button>
                </div>
            </div>`;
        });
        if (total > items.length) html += `<div class="ss-sub" style="margin-top:8px">… și încă ${total - items.length}.</div>`;
        container.innerHTML = html;
        const dupaId = (btn: Element) => items.find(x => x.id === parseInt((btn as HTMLElement).dataset.wfId || '0'));
        container.querySelectorAll('.brf-refuz').forEach(btn => {
            (btn as HTMLElement).addEventListener('click', () => { const b = dupaId(btn); if (b) _refuzaPropunerea(b); });
        });
        container.querySelectorAll('.brf-lucrare').forEach(btn => {
            (btn as HTMLElement).addEventListener('click', () => { const b = dupaId(btn); if (b) _propunereaLaLucrare(b); });
        });
        return;
    }

    if (tab === 'workflows') {
        const items = _pendingData.workflows;
        if (items.length === 0) {
            const nb = Number((_pendingData as any).briefing_total || 0);
            container.innerHTML = '<div class="alarm-empty"><span class="icon">✅</span> Toate workflow-urile sunt validate!'
                + (nb ? `<div class="ss-sub" style="margin-top:6px">…dar briefingul are <b>${nb}</b> propuneri în așteptare — vezi fila „Propuneri briefing".</div>` : '')
                + '</div>';
            return;
        }
        const totalWf = _pendingData.workflows_total ?? items.length;
        let html = totalWf > items.length
            ? `<div class="ss-sub" style="margin-bottom:8px">Arăt cele mai noi ${items.length} din ${totalWf}.</div>` : '';
        items.forEach(w => {
            const duration = w.duration_minutes ? w.duration_minutes + ' min' : '—';
            html += `<div class="val-card" data-wf-id="${w.id}">
                <div class="val-card-header">
                    <strong>${escHtml(w.action_ro || 'Mentenanță')}</strong>
                    <span class="val-card-date">${escHtml(formatDateTimeRO(w.completed_at))}</span>
                </div>
                <div class="val-card-meta">
                    ${escHtml(w.machine_name || w.machine_id)} · Operator: ${escHtml(w.operator_name || '—')} · ${duration} · ${w.steps_completed}/${w.steps_total} pași
                </div>
                ${w.coleg_nota && w.coleg_nota.startsWith('De revizuit') ? `<div class="val-card-meta" style="color:var(--text-muted)">🤝 ${escHtml(w.coleg_nota)}</div>` : ''}
                <div class="val-card-actions" style="margin-top:8px;display:flex;gap:6px">
                    <button class="btn btn-xs btn-green val-approve-wf" data-wf-id="${w.id}">✓ Aprobă</button>
                    <button class="btn btn-xs btn-red val-reject-wf" data-wf-id="${w.id}">✕ Respinge</button>
                    <button class="btn btn-xs btn-outline val-detail-wf" data-wf-id="${w.id}">📋 Detalii</button>
                </div>
            </div>`;
        });
        container.innerHTML = html;
        container.querySelectorAll('.val-approve-wf').forEach(btn => {
            (btn as HTMLElement).addEventListener('click', () => _validateWorkflow(parseInt((btn as HTMLElement).dataset.wfId || '0'), 'approved'));
        });
        container.querySelectorAll('.val-reject-wf').forEach(btn => {
            (btn as HTMLElement).addEventListener('click', () => _validateWorkflow(parseInt((btn as HTMLElement).dataset.wfId || '0'), 'rejected'));
        });
        container.querySelectorAll('.val-detail-wf').forEach(btn => {
            (btn as HTMLElement).addEventListener('click', () => _showWorkflowDetail(parseInt((btn as HTMLElement).dataset.wfId || '0')));
        });
    } else if (tab === 'enrichments') {
        const items = _pendingData.enrichments || [];
        if (items.length === 0) {
            container.innerHTML = '<div class="alarm-empty"><span class="icon">✅</span> Nicio completare în așteptare!</div>';
            return;
        }
        let html = '';
        items.forEach(e => {
            const typeLabel = e.content_type === 'step' ? 'Pas suplimentar'
                : e.content_type === 'photo' ? 'Foto' : e.content_type === 'video' ? 'Video' : 'Notă';
            html += `<div class="val-card" data-enr-id="${e.id}">
                <div class="val-card-header">
                    <strong>${escHtml(typeLabel)} — ${escHtml(e.action_ro || 'Mentenanță')}</strong>
                    <span class="val-card-date">${escHtml(formatDateTimeRO(e.created_at))}</span>
                </div>
                <div class="val-card-meta">
                    ${escHtml(e.machine_name || e.machine_id || '')} · ${escHtml(e.created_by || '')}
                    ${e.text_content ? '<br>' + escHtml(truncate(e.text_content, 400)) : ''}
                </div>
                ${e.file_url && (e.content_type === 'photo' || e.content_type === 'video') ? `<div class="val-det-poze">${
                    // 30 sep 2026: poza / filmarea propusă nu se vedea deloc — se aproba pe încredere
                    e.content_type === 'video'
                        ? `<video class="val-det-media" src="${escHtml(e.file_url)}" controls preload="none"></video>`
                        : `<a href="${escHtml(e.file_url)}" target="_blank" rel="noopener"><img class="val-det-media" src="${escHtml(e.file_url)}" alt="completare propusă" loading="lazy"></a>`}</div>` : ''}
                <div class="val-card-actions" style="margin-top:8px;display:flex;gap:6px;flex-wrap:wrap">
                    <button class="btn btn-xs btn-green val-approve-enr" data-enr-id="${e.id}">✓ Validează</button>
                    <button class="btn btn-xs btn-red val-reject-enr" data-enr-id="${e.id}">✕ Respinge</button>
                    ${e.workflow_id ? `<button class="btn btn-xs btn-outline val-detail-wf" data-wf-id="${e.workflow_id}">📋 Workflow</button>` : ''}
                </div>
            </div>`;
        });
        container.innerHTML = html;
        container.querySelectorAll('.val-approve-enr').forEach(btn => {
            (btn as HTMLElement).addEventListener('click', () => _reviewEnrichment(parseInt((btn as HTMLElement).dataset.enrId || '0'), 'approved'));
        });
        container.querySelectorAll('.val-reject-enr').forEach(btn => {
            (btn as HTMLElement).addEventListener('click', () => _reviewEnrichment(parseInt((btn as HTMLElement).dataset.enrId || '0'), 'rejected'));
        });
        container.querySelectorAll('.val-detail-wf').forEach(btn => {
            (btn as HTMLElement).addEventListener('click', () => _showWorkflowDetail(parseInt((btn as HTMLElement).dataset.wfId || '0')));
        });
    } else {
        const items = _pendingData.logs;
        if (items.length === 0) {
            container.innerHTML = '<div class="alarm-empty"><span class="icon">✅</span> Toate intervențiile sunt validate!</div>';
            return;
        }
        // filtru vechime + arhivare în bloc (audit UX 30 iul 2026): backlog-ul
        // istoric nu mai îngroapă lucrul curent al inginerului
        const nD7 = items.filter(l => _valAgeDays(l.date) <= 7).length;
        const nD830 = items.filter(l => { const a = _valAgeDays(l.date); return a > 7 && a <= 30; }).length;
        const nGt30 = items.filter(l => _valAgeDays(l.date) > 30).length;
        const chip = (key: string, label: string, n: number) =>
            `<button class="btn btn-xs val-age-chip ${_valAgeFilter === key ? 'btn-primary' : 'btn-outline'}" data-age="${key}" >${label} (${n})</button>`;
        let html = `<div style="display:flex;gap:6px;flex-wrap:wrap;margin-bottom:10px;align-items:center">
            ${chip('toate', 'Toate', items.length)}${chip('d7', '≤7 zile', nD7)}${chip('d830', '8–30 zile', nD830)}${chip('gt30', '>30 zile', nGt30)}
            ${nGt30 > 0 ? `<button class="btn btn-xs btn-outline" id="val-bulk-archive" style="margin-left:auto;color:var(--text-muted)">🗄 Arhivează ca istoric (${nGt30})</button>` : ''}
        </div>`;
        const visible = items.filter(l => {
            const a = _valAgeDays(l.date);
            return _valAgeFilter === 'toate' || (_valAgeFilter === 'd7' && a <= 7)
                || (_valAgeFilter === 'd830' && a > 7 && a <= 30) || (_valAgeFilter === 'gt30' && a > 30);
        });
        const totalLog = _pendingData.logs_total ?? items.length;
        if (totalLog > items.length) html += `<div class="ss-sub" style="margin-bottom:8px">Arăt cele mai noi ${items.length} din ${totalLog}.</div>`;
        if (visible.length === 0) html += '<div class="alarm-empty"><span class="icon">🔍</span> Nimic în acest interval de vechime.</div>';
        /* Detaliul intervenției, cerut de Dan pe 30 aug 2026: «dacă vreau să
         * validez, n-am detalii să văd ce s-a făcut». Titlul era tăiat la 80 de
         * caractere — exact acolo unde începe «Acțiuni: …». Restul câmpurilor
         * erau completate în jurnal, dar nu ajungeau pe ecran.
         * Deschis la clic, nu din start: lista poate avea o sută de intervenții
         * vechi, și n-are rost să le desfășori pe toate ca să citești una. */
        const _rand = (eticheta: string, valoare: any) => valoare
            ? `<div class="val-det-rand"><span class="val-det-et">${eticheta}</span>
                 <span class="val-det-val">${escHtml(String(valoare))}</span></div>`
            : '';
        // 30 sep 2026 (Dan: „la validare n-am putut verifica ce s-a făcut”): raportul are părți —
        // problema, „Constatări:”, „Acțiuni:” (cu zilele de lucru) — și se arată pe părți, deschis din
        // start când lista e scurtă; toate pozele, nu doar una; fără nimic scris → avertisment.
        const _parti = (text: string) => {
            const t = String(text || '');
            const iC = t.search(/\n\nConstatări:[ \n]/), iA = t.search(/\n\nAcțiuni:[ \n]/);
            const capat = [iC, iA].filter(i => i >= 0);
            const problema = (capat.length ? t.slice(0, Math.min(...capat)) : t).trim();
            const constatari = iC >= 0 ? t.slice(iC).replace(/^\n\nConstatări:[ \n]/, '').split(/\n\nAcțiuni:[ \n]/)[0].trim() : '';
            const actiuni = iA >= 0 ? t.slice(iA).replace(/^\n\nAcțiuni:[ \n]/, '').trim() : '';
            return { problema, constatari, actiuni };
        };
        const _bloc = (eticheta: string, valoare: string) => valoare
            ? `<div class="val-det-bloc"><div class="val-det-et">${eticheta}</div><div class="val-det-text">${escHtml(valoare)}</div></div>` : '';
        const deschise = visible.length <= 10;
        visible.forEach(l => {
            const parti = _parti(l.action || '');
            const titluScurt = truncate(parti.problema || l.action || '', 80);
            const nimicScris = !parti.constatari && !parti.actiuni && !l.notes && !l.cause_found;
            l._nimicScris = nimicScris;
            const areMaiMult = true;
            const _ora = (t: string) => { const d = new Date(String(t).includes('T') ? t : String(t).replace(' ', 'T')); return isNaN(d.getTime()) ? String(t) : formatDateTimeRO(d); };
            const ore = [l.start_time, l.completed_time].filter(Boolean).map(_ora).join(' → ');
            const durata = l.duration_minutes
                ? (l.duration_minutes >= 60 ? `${Math.floor(l.duration_minutes / 60)} h ${l.duration_minutes % 60} min` : `${l.duration_minutes} min`) : '';
            const poze = (l.media || []).map((m: any) => m.media_type === 'video'
                ? `<video class="val-det-media" src="${escHtml(m.url)}" controls preload="none"></video>`
                : `<a href="${escHtml(m.url)}" target="_blank" rel="noopener"><img class="val-det-media" src="${escHtml(m.url)}" alt="poză de la intervenție" loading="lazy"></a>`).join('');
            // ce fel de lucrare e (30 sep 2026): fila strânge urgențe, lucrări programate de inginer
            // și verificări CRITICE din plan bifate cu „Am făcut" — cardul spune care e care
            const fel = l.category === 'urgent' ? 'Urgență'
                : (l.task_assigned_by ? 'Programată de ' + l.task_assigned_by
                    : (l.interval_id ? (l.critical ? 'Din plan — CRITICĂ' : 'Din plan') : 'Consemnată'));
            const cererea = l.task_notes ? `<div class="val-card-meta"><em>Cerut: ${escHtml(truncate(l.task_notes, 160))}</em>${l.task_due_date ? ' · pe ' + escHtml(l.task_due_date) : ''}</div>` : '';
            html += `<div class="val-card" data-log-id="${l.id}">
                <div class="val-card-header">
                    <strong>${escHtml(l.registration_number || '')} — ${escHtml(titluScurt)}</strong>
                    <span class="val-card-date"><span class="val-fel${l.critical ? ' val-fel-critic' : ''}">${escHtml(fel)}</span> ${escHtml(l.date || '')}</span>
                </div>
                ${cererea}
                <div class="val-card-meta">
                    ${escHtml(l.machine_name || l.machine_id)} · Tehnician: ${escHtml(l.technician || '—')}
                    ${l.duration_minutes ? ' · ' + l.duration_minutes + ' min' : ''}
                    ${l.cause_found ? ' · Cauză: ' + escHtml(truncate(l.cause_found, 60)) : ''}
                </div>
                ${areMaiMult ? `
                <button class="val-det-buton" data-det="${l.id}" aria-expanded="${deschise}">
                    ${deschise ? '▴ Ascunde detaliile' : '▾ Ce s-a făcut'}</button>
                <div class="val-det" id="val-det-${l.id}" ${deschise ? '' : 'hidden'}>
                    ${nimicScris ? `<div class="val-det-gol">⚠ Nu s-a scris ce s-a constatat sau ce s-a făcut — cere completarea înainte de validare.</div>` : ''}
                    ${_bloc('Problema', parti.problema)}
                    ${_bloc('Ce s-a constatat', parti.constatari)}
                    ${_bloc('Ce s-a făcut', parti.actiuni)}
                    ${_rand('Cauza găsită', l.cause_found)}
                    ${_rand('Observații', l.notes)}
                    ${(l.validation_notes || '').startsWith('De revizuit') ? _rand('Colegul AI', l.validation_notes) : ''}
                    ${_rand('Piese folosite', l.parts_used)}
                    ${_rand('Au participat', l.involved_persons)}
                    ${_rand('Durată lucrată', durata)}
                    ${_rand('Ore', ore)}
                    ${poze ? `<div class="val-det-bloc"><div class="val-det-et">Poze și filmări (${(l.media || []).length})</div><div class="val-det-poze">${poze}</div></div>` : ''}
                    ${_rand('Contor la intervenție', l.hours_at_service
                        ? Math.round(Number(l.hours_at_service)) + ' h' : '')}
                    ${_rand('Categorie', l.category)}
                    ${l.recording_filename && !(l.media || []).length ? (() => {
                        // Aceeași rută ca în jurnal (journal.ts:872): filmarea se
                        // cere după ID-ul lucrării, nu după numele fișierului.
                        const ext = String(l.recording_filename).split('.').pop().toLowerCase();
                        const url = '/api/maintenance_log/' + l.id + '/recording';
                        if (['mp4', 'webm', 'mov', 'avi', 'mkv'].includes(ext)) {
                            return `<div class="val-det-rand"><span class="val-det-et">Filmare</span>
                                <video class="val-det-media" src="${url}" controls preload="none"></video></div>`;
                        }
                        if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
                            return `<div class="val-det-rand"><span class="val-det-et">Poză</span>
                                <img class="val-det-media" src="${url}" alt="poză de la intervenție"></div>`;
                        }
                        return `<div class="val-det-rand"><span class="val-det-et">Atașament</span>
                            <a class="val-det-val" target="_blank" href="${url}">📎 descarcă</a></div>`;
                    })() : ''}
                </div>` : ''}
                <div class="val-card-actions" style="margin-top:8px;display:flex;gap:6px">
                    <button class="btn btn-xs btn-green val-approve-log" data-log-id="${l.id}">✓ Validează</button>
                    <button class="btn btn-xs btn-red val-reject-log" data-log-id="${l.id}">✕ Respinge</button>
                </div>
            </div>`;
        });
        container.innerHTML = html;
        container.querySelectorAll('.val-det-buton').forEach(btn => {
            (btn as HTMLElement).addEventListener('click', () => {
                const id = (btn as HTMLElement).dataset.det;
                const panou = document.getElementById('val-det-' + id);
                if (!panou) return;
                const deschis = !panou.hidden;
                panou.hidden = deschis;
                btn.setAttribute('aria-expanded', String(!deschis));
                btn.textContent = (deschis ? '▾ Ce s-a făcut' : '▴ Ascunde detaliile');
            });
        });
        container.querySelectorAll('.val-age-chip').forEach(btn => {
            (btn as HTMLElement).addEventListener('click', () => {
                _valAgeFilter = ((btn as HTMLElement).dataset.age || 'toate') as typeof _valAgeFilter;
                _renderValidationTab('logs');
            });
        });
        document.getElementById('val-bulk-archive')?.addEventListener('click', async () => {
            const ok = await askConfirm(`Arhivezi ${nGt30} intervenții vechi ca istoric?`,
                'Cele mai vechi de 30 de zile se marchează «auto» cu nota „Arhivat în bloc ca istoric".\nNu se șterge nimic — rămân în istoric și în rapoarte.', 'Arhivează');
            if (!ok) return;
            try {
                const r = await fetch('/api/pending-validations/arhiveaza-vechi', {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ zile: 30 }),
                });
                const d = await r.json();
                if (d.status === 'ok') {
                    toast(`${d.arhivate} intervenții arhivate ca istoric`, 'success');
                    _valAgeFilter = 'toate';
                    await _fetchAndRenderValidations();
                    updateValidationBadge();
                } else toast(d.error || 'Eroare', 'error');
            } catch (e) { toast('Eroare rețea', 'error'); }
        });
        container.querySelectorAll('.val-approve-log').forEach(btn => {
            (btn as HTMLElement).addEventListener('click', () => {
                const id = parseInt((btn as HTMLElement).dataset.logId || '0');
                _validateLog(id, 'validated', !!(visible.find((x: any) => x.id === id) as any)?._nimicScris);
            });
        });
        container.querySelectorAll('.val-reject-log').forEach(btn => {
            (btn as HTMLElement).addEventListener('click', () => _validateLog(parseInt((btn as HTMLElement).dataset.logId || '0'), 'rejected'));
        });
        // detectare mentenanțe planificate care seamănă (cerința Dan 30 iul:
        // „mașina desfăcută → fac mai multe mentenanțe; inginerul decide legarea")
        visible.forEach(l => {
            const card = container.querySelector(`.val-card[data-log-id="${l.id}"]`) as HTMLElement | null;
            if (card) incarcaSugestiiInterval(l.id, _creeazaBlocSugestii(card));
        });
    }
}

function _etichetaScadenta(zile: number | null): string {
    if (zile === null || zile === undefined) return '';
    if (zile < 0) return `· depășită cu ${-zile} zile`;
    if (zile === 0) return '· scadentă AZI';
    return `· urmează în ~${zile} zile`;
}

function _creeazaBlocSugestii(parent: HTMLElement): HTMLElement {
    let bloc = parent.querySelector(':scope > .val-sugg') as HTMLElement | null;
    if (!bloc) {
        bloc = document.createElement('div');
        bloc.className = 'val-sugg';
        bloc.style.cssText = 'display:none;margin-top:8px;padding:8px 10px;background:rgba(0,180,255,.05);border:1px dashed rgba(0,180,255,.3);border-radius:8px;font-size:.83rem';
        parent.appendChild(bloc);
    }
    return bloc;
}

/** Sugestii de legare log↔interval, randate în `bloc` (inbox validare + istoric). */
export async function incarcaSugestiiInterval(logId: number, bloc: HTMLElement): Promise<void> {
    try {
        const r = await fetch(`/api/maintenance_log/${logId}/interval_suggestions`);
        if (!r.ok) return;
        const d = await r.json();
        /* Intervalul de care lucrarea E DEJA legată rămâne în listă, marcat
           „✓ legată" — înainte era filtrat afară, deci apăsai „🔗 Leagă" și rândul
           pur și simplu DISPĂREA. Toast-ul se stinge în câteva secunde, iar pe
           ecran nu mai rămânea nicio urmă că legarea a reușit: aceeași lipsă de
           feedback care, pe 3 august, a produs legături duble din clickuri
           repetate. Serverul îl marchează deja `legat_deja`. */
        const sugestii = (d.sugestii || []);
        const nelegate = sugestii.filter((s: any) => !s.legat_deja);
        if (d.interval_curent && sugestii.length === 0) {
            bloc.innerHTML = '✅ Legată de o mentenanță planificată — contorul ei s-a resetat.';
            bloc.style.display = '';
            return;
        }
        if (sugestii.length === 0) { bloc.style.display = 'none'; return; }
        let html = nelegate.length === 0
            ? '<div style="color:var(--green,#22c55e);margin-bottom:6px">✅ Toate mentenanțele detectate sunt legate — contoarele s-au resetat.</div>'
            : '<div style="color:var(--cyan);margin-bottom:6px">🔧 Seamănă cu mentenanța planificată:</div>';
        sugestii.forEach((s: any) => {
            // 3 aug 2026: legată = ✓ vizibil, nu buton activ (lipsa feedback-ului
            // ducea la click-uri repetate și dubluri)
            const actiune = s.legat_deja
                ? '<span style="color:var(--green,#22c55e);font-weight:700;padding:6px 10px">✓ legată</span>'
                : `<button class="btn btn-xs btn-outline val-sugg-link" data-iv-id="${s.interval_id}" >🔗 Leagă</button>`;
            html += `<div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;padding:4px 0${s.legat_deja ? ';opacity:.75' : ''}">
                <span style="flex:1;min-width:160px">${escHtml(truncate(s.action_ro || '', 90))}
                    <span style="color:var(--text-muted)">(${escHtml(s.interval_text || '')} ${escHtml(s.stare_text || _etichetaScadenta(s.zile_pana_la_scadenta))})</span></span>
                ${actiune}
            </div>`;
        });
        bloc.innerHTML = html;
        bloc.style.display = '';
        bloc.querySelectorAll('.val-sugg-link').forEach(btn => {
            (btn as HTMLElement).addEventListener('click', async () => {
                const ivId = parseInt((btn as HTMLElement).dataset.ivId || '0');
                const ok = await askConfirm('Legi lucrarea de această mentenanță planificată?',
                    'Contorul mentenanței se resetează: următoarea scadență se calculează de la această lucrare.\nDacă lucrarea e deja legată de alt interval, se creează o înregistrare suplimentară (aceeași oprire).',
                    'Leagă');
                if (!ok) return;
                try {
                    const resp = await fetch(`/api/maintenance_log/${logId}/link_interval`, {
                        method: 'POST', headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ interval_id: ivId }),
                    });
                    const res = await resp.json();
                    if (res.status === 'ok') {
                        toast(res.mod === 'suplimentar'
                            ? 'Înregistrare suplimentară creată — contorul resetat'
                            : 'Legat — contorul mentenanței s-a resetat', 'success');
                        incarcaSugestiiInterval(logId, bloc);
                    } else toast(res.error || 'Eroare', 'error');
                } catch (e) { toast('Eroare rețea', 'error'); }
            });
        });
    } catch (e) { /* sugestiile sunt opționale — tăcut */ }
}

/* ── Decizia pe propunerile din briefing (29 sep 2026) ─────────────── */

function _textPropunere(notes: string): { nr: string; actiune: string; motiv: string } {
    const m = String(notes || '').match(/^\[Auto-generat din briefing #(\d+)\]\s*(.*)$/s);
    const [actiune, motiv] = String(m ? m[2] : notes || '').split('|').map(x => (x || '').trim());
    return { nr: m ? m[1] : '', actiune: actiune || '', motiv: motiv || '' };
}

async function _dupaDecizie(r: Response, mesaj: string): Promise<void> {
    let d: any = {};
    try { d = await r.json(); } catch { /* răspuns gol */ }
    if (!r.ok) { toast(d.error || `Eroare (${r.status})`, 'error'); return; }
    toast(mesaj, 'success');
    _fetchAndRenderValidations();
    updateValidationBadge();
}

async function _refuzaPropunerea(b: any): Promise<void> {
    const { actiune } = _textPropunere(b.notes);
    const motiv = await askText('Refuz propunerea', {
        label: actiune + '\n\nDe ce o refuzi? Motivul rămâne pe propunere și ajunge în Hub.',
        required: true, multiline: true, okText: 'Refuz',
        placeholder: 'ex.: e deja în Restante / nu e treabă de mentenanță / s-a rezolvat',
    });
    if (motiv === null) return;
    try {
        const r = await fetch(`/api/briefing/${b.id}/refuza`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ motiv }),
        });
        await _dupaDecizie(r, 'Propunere refuzată.');
    } catch { toast('Eroare rețea', 'error'); }
}

async function _propunereaLaLucrare(b: any): Promise<void> {
    const { actiune, motiv } = _textPropunere(b.notes);
    const [masini, colegi] = await Promise.all([
        _ensureMachineCache().catch(() => [] as any[]),
        api('/api/operator_tasks/colegi').catch(() => [] as any[]),
    ]);
    // utilajul pomenit primul în text („pe M3 …") vine preselectat, dacă există
    const pomenit = (actiune.match(/\b(M\d{1,2})\b/) || [])[1] || '';
    const eu = state.currentUser?.username || '';
    const optMasini = (masini || []).map((m: any) =>
        `<option value="${escHtml(m.id)}"${m.id === pomenit ? ' selected' : ''}>${escHtml(m.id)} — ${escHtml(m.name || '')}</option>`).join('');
    const optOameni = `<option value="">— nealocată (o vede toată lumea) —</option>`
        + (eu ? `<option value="${escHtml(eu)}">${escHtml(eu)} (eu)</option>` : '')
        + (colegi || []).map((c: any) => `<option value="${escHtml(c.username)}">${escHtml(c.display_name || c.username)}</option>`).join('');
    const overlay = document.createElement('div');
    overlay.className = 'proc-overlay';
    overlay.innerHTML = `
      <div class="proc-container" style="max-width:520px" role="dialog" aria-modal="true">
        <div class="proc-header"><div class="proc-header-top">
          <h2 class="proc-title">Fă din propunere o lucrare</h2>
          <button class="proc-close" id="brf-x">&times;</button></div></div>
        <div style="padding:16px;display:flex;flex-direction:column;gap:10px">
          ${motiv ? `<div style="color:var(--text-muted);font-size:.85rem">${escHtml(truncate(motiv, 300))}</div>` : ''}
          <label style="font-size:.85rem">Ce se face
            <textarea class="dx-input" id="brf-desc" rows="3" style="min-height:70px">${escHtml(actiune)}</textarea></label>
          <label style="font-size:.85rem">Utilaj
            <select class="dx-input" id="brf-masina" style="min-height:44px"><option value="">— alege —</option>${optMasini}</select></label>
          <label style="font-size:.85rem">Cine
            <select class="dx-input" id="brf-cine" style="min-height:44px">${optOameni}</select></label>
          <label style="font-size:.85rem">Data
            <input class="dx-input" id="brf-zi" type="date" value="${todayStr()}" style="min-height:44px"></label>
          <div style="display:flex;gap:10px;justify-content:flex-end">
            <button class="dx-btn-primary" id="brf-renunta" style="background:var(--card-bg);border:1px solid var(--border);color:var(--text-muted);padding:12px 22px;min-height:44px">Renunță</button>
            <button class="dx-btn-primary" id="brf-ok" style="padding:12px 26px;min-height:44px">Creează lucrarea</button>
          </div>
        </div>
      </div>`;
    document.body.appendChild(overlay);
    const inchide = () => overlay.remove();
    overlay.querySelector('#brf-x')!.addEventListener('click', inchide);
    overlay.querySelector('#brf-renunta')!.addEventListener('click', inchide);
    overlay.addEventListener('click', e => { if (e.target === overlay) inchide(); });
    overlay.querySelector('#brf-ok')!.addEventListener('click', async () => {
        const masina = (overlay.querySelector('#brf-masina') as HTMLSelectElement).value;
        const zi = (overlay.querySelector('#brf-zi') as HTMLInputElement).value;
        if (!masina) { toast('Alege utilajul.', 'warning'); return; }
        if (!zi) { toast('Alege data.', 'warning'); return; }
        try {
            const r = await fetch(`/api/briefing/${b.id}/lucrare`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    machine_id: masina, due_date: zi,
                    assigned_to: (overlay.querySelector('#brf-cine') as HTMLSelectElement).value,
                    description: (overlay.querySelector('#brf-desc') as HTMLTextAreaElement).value.trim(),
                }),
            });
            if (r.ok) inchide();
            await _dupaDecizie(r, 'Lucrare creată din propunere.');
        } catch { toast('Eroare rețea', 'error'); }
    });
}

/** Respingerea nu mai e un capăt de drum (30 sep 2026): lucrarea respinsă nu revenea nicăieri —
 *  nici la mecanic, nici în calendar; refacerea exista doar îngropată în Rapoarte. Imediat după
 *  „Respinge", inginerul poate programa refacerea: o lucrare „DE REFĂCUT" în „Lucrările mele". */
async function _propuneRefacerea(fel: 'workflow' | 'log', id: number, info: any, motiv: string): Promise<void> {
    const m = new Date(); m.setDate(m.getDate() + 1);
    const cine = String(info?.technician || info?.operator_name || '').trim();
    const zi = await askText('Programezi refacerea?', {
        label: 'Lucrarea respinsă devine o lucrare nouă „DE REFĂCUT", urgentă, cu motivul tău'
            + (cine ? ', în „Lucrările mele" la ' + cine : '') + '. Alege ziua — sau „Nu acum".',
        type: 'date', value: localDateKey(m), okText: 'Programează', required: true,
    });
    if (!zi) return;
    try {
        if (fel === 'workflow') {
            const d = await api('/api/maintenance_workflows/' + id + '/redo', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ observations: motiv, due_date: zi }),
            });
            toast('Refacerea e programată pe ' + zi + (d?.assigned_to ? ' la ' + d.assigned_to : ' (nealocată)'), 'success');
        } else {
            let useri: string[] = [];
            try { useri = await api('/api/auth/usernames'); } catch (_e) { /* fără listă → nealocată */ }
            const cui = (useri || []).includes(cine) ? cine : '';
            const t = String(info?.action || '');
            const problema = t.split(/\n\n(?:Constatări|Acțiuni):[ \n]/)[0].trim() || 'Lucrare';
            await api('/api/operator_tasks', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    machine_id: info?.machine_id, assigned_to: cui, due_date: zi, task_type: 'assigned',
                    priority: 'high', description: 'DE REFĂCUT — ' + truncate(problema, 160),
                    notes: 'Respinsă la validare (' + (info?.registration_number || '#' + id) + '): ' + motiv,
                }),
            });
            toast('Refacerea e programată pe ' + zi + (cui ? ' la ' + cui : ' (nealocată — o găsești în Calendar)'), 'success');
        }
    } catch (e: any) { toast('Refacerea nu s-a programat: ' + (e?.message || e), 'error'); }
}

async function _validateWorkflow(wfId: number, status: 'approved' | 'rejected'): Promise<boolean> {
    const comment = await askText(status === 'approved' ? 'Aprobare workflow' : 'Respingere workflow',
        { label: status === 'approved' ? 'Observații (opțional):' : 'Motiv respingere (obligatoriu):',
          required: status === 'rejected', multiline: true,
          okText: status === 'approved' ? 'Aprobă' : 'Respinge' });
    if (comment === null) return false;
    try {
        const r = await fetch('/api/maintenance_workflows/' + wfId + '/validate', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ validation_status: status, comment: comment, validated_by: state.currentUser?.username || 'inginer' })
        });
        const d = await r.json();
        if (d.ok) {
            toast(status === 'approved' ? 'Workflow aprobat!' : 'Workflow respins.', status === 'approved' ? 'success' : 'info');
            const wf = (_pendingData?.workflows || []).find((w: any) => w.id === wfId);
            _fetchAndRenderValidations();
            updateValidationBadge();
            if (status === 'rejected') await _propuneRefacerea('workflow', wfId, wf, comment);
            return true;
        }
        toast(d.error || 'Eroare', 'error');
    } catch (e) { toast('Eroare rețea', 'error'); }
    return false;
}

async function _reviewEnrichment(enrichmentId: number, status: 'approved' | 'rejected'): Promise<void> {
    const notes = await askText(status === 'approved' ? 'Aprobare completare' : 'Respingere completare',
        { label: status === 'approved' ? 'Comentariu validare (opțional):' : 'Motiv respingere (obligatoriu):',
          required: status === 'rejected', multiline: true,
          okText: status === 'approved' ? 'Aprobă' : 'Respinge' });
    if (notes === null) return;
    try {
        const r = await fetch('/api/work/enrichments/' + enrichmentId + '/review', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ status: status, review_notes: notes.trim() || null }),
        });
        const d = await r.json();
        if (d.ok) {
            toast(status === 'approved' ? 'Completare aprobată!' : 'Completare respinsă.', status === 'approved' ? 'success' : 'info');
            _fetchAndRenderValidations();
            updateValidationBadge();
        } else toast(d.error || 'Eroare', 'error');
    } catch (e) { toast('Eroare rețea', 'error'); }
}

async function _validateLog(logId: number, status: 'validated' | 'rejected', nimicScris = false): Promise<void> {
    if (status === 'validated' && nimicScris && !await askConfirm('Validezi fără să știi ce s-a făcut?',
        'La intervenția asta nu s-a scris nici ce s-a constatat, nici ce s-a făcut. De obicei se respinge cu „completează raportul”.',
        'Validează oricum')) return;
    const notes = await askText(status === 'validated' ? 'Validare intervenție' : 'Respingere intervenție',
        { label: status === 'validated' ? 'Observații validare (opțional):' : 'Motiv respingere (obligatoriu):',
          required: status === 'rejected', multiline: true,
          okText: status === 'validated' ? 'Validează' : 'Respinge' });
    if (notes === null) return;
    try {
        const r = await fetch('/api/maintenance_log/' + logId + '/validate', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ status: status, validation_notes: notes, validated_by: state.currentUser?.username || 'inginer' })
        });
        const d = await r.json();
        if (d.ok || d.status === 'ok') {
            toast(status === 'validated' ? 'Intervenție validată!' : 'Intervenție respinsă.', status === 'validated' ? 'success' : 'info');
            const l = (_pendingData?.logs || []).find((x: any) => x.id === logId);
            _fetchAndRenderValidations();
            if (status === 'rejected') await _propuneRefacerea('log', logId, l, notes);
            updateValidationBadge();
        } else toast(d.error || 'Eroare', 'error');
    } catch (e) { toast('Eroare rețea', 'error'); }
}

async function _showWorkflowDetail(wfId: number): Promise<void> {
    try {
        const [wfResp, pdfResp] = await Promise.all([
            fetch('/api/maintenance_workflows/' + wfId),
            fetch('/api/maintenance_workflows/' + wfId + '/pdf_context')
        ]);
        const wf = await wfResp.json();
        const pdfCtx = await pdfResp.json();
        if (!wf || !wf.id) { toast('Workflow negăsit', 'error'); return; }

        let stepsHtml = '';
        try {
            const stepsData = JSON.parse(wf.steps_data || wf.execution_data || '[]');
            if (Array.isArray(stepsData) && stepsData.length > 0) {
                stepsHtml = '<h4>Pași executați:</h4><ol class="val-steps-list">';
                stepsData.forEach((s, i) => {
                    const icon = s.done ? '✅' : '⬜';
                    const duration = s.duration_seconds ? ` <span class="val-step-dur">(${Math.round(s.duration_seconds / 60)} min)</span>` : '';
                    const pageLink = (pdfCtx.procedure_steps && pdfCtx.procedure_steps[i] && pdfCtx.procedure_steps[i].document_id && pdfCtx.procedure_steps[i].document_page)
                        ? ` <button class="val-step-page-btn" data-doc="${pdfCtx.procedure_steps[i].document_id}" data-page="${pdfCtx.procedure_steps[i].document_page}">p.${pdfCtx.procedure_steps[i].document_page}</button>`
                        : '';
                    stepsHtml += `<li>${icon} ${escHtml(s.text || '')}${duration}${pageLink}</li>`;
                });
                stepsHtml += '</ol>';
            }
        } catch (e) {}

        let notesHtml = '';
        try {
            const notesList = JSON.parse(wf.notes || '[]');
            if (Array.isArray(notesList) && notesList.length > 0) {
                notesHtml = '<h4>Note operator:</h4><ul class="val-notes-list">';
                notesList.forEach(n => {
                    notesHtml += `<li>${escHtml(n.text || '')} <span style="color:var(--text-muted);font-size:0.85em">(${n.by || ''}, pas ${n.step || '—'}, ${_timeAgo(n.at || '')})</span></li>`;
                });
                notesHtml += '</ul>';
            }
        } catch (e) {}

        let mediaHtml = '';
        if (wf.media && wf.media.length > 0) {
            mediaHtml = '<h4>Media:</h4><div style="display:flex;gap:6px;flex-wrap:wrap">';
            wf.media.forEach(m => {
                if (m.media_type === 'image') {
                    mediaHtml += `<img src="/uploads/${escHtml(m.file_path)}" style="max-width:150px;border-radius:6px;cursor:pointer" loading="lazy" onclick="window.open(this.src,'_blank')">`;
                }
            });
            mediaHtml += '</div>';
        }

        const hasPdf = pdfCtx.documents && pdfCtx.documents.length > 0;
        const pdfDoc = hasPdf ? pdfCtx.documents[0] : null;
        const pdfPageStart = pdfDoc ? (pdfDoc.page || 1) : 1;

        let pdfSelectorHtml = '';
        if (hasPdf && pdfCtx.documents.length > 1) {
            pdfSelectorHtml = '<select class="val-pdf-selector">';
            pdfCtx.documents.forEach((d: any, i: number) => {
                pdfSelectorHtml += `<option value="${i}" ${i === 0 ? 'selected' : ''}>${escHtml(d.filename || 'Document ' + (i + 1))} — p.${d.page || 1}</option>`;
            });
            pdfSelectorHtml += '</select>';
        }

        let pdfExtractInfo = '';
        if (pdfCtx.extracts && pdfCtx.extracts.length > 0) {
            const ext = pdfCtx.extracts[0];
            pdfExtractInfo = `<div class="val-pdf-extract-info">📖 ${escHtml(ext.title || '')} ${ext.start_page != null ? '(p.' + (ext.start_page + 1) + (ext.end_page != null ? '-' + (ext.end_page + 1) : '') + ')' : ''}</div>`;
        }

        const detailEl = document.createElement('div');
        detailEl.className = 'val-detail-overlay';
        detailEl.innerHTML = `
        <div class="val-split-container ${hasPdf ? 'val-has-pdf' : 'val-no-pdf'}">
            <div class="val-split-left">
                <div class="val-split-header">
                    <h3>${escHtml(wf.action_ro || 'Workflow')}</h3>
                    <button class="btn btn-sm btn-outline val-detail-close" title="Închide">✕</button>
                </div>
                <div class="val-detail-meta">
                    Utilaj: <strong>${escHtml(wf.machine_name || wf.machine_id)}</strong> · Operator: ${escHtml(wf.operator_name || '—')}
                    · Durată: ${wf.duration_minutes || 0} min · Status: ${escHtml(wf.status || '')}
                </div>
                <div class="val-split-body">
                    ${stepsHtml}
                    ${notesHtml}
                    ${mediaHtml}
                </div>
                <div class="val-split-actions">
                    <button class="btn btn-green val-detail-approve" data-wf-id="${wf.id}">✓ Aprobă</button>
                    <button class="btn btn-red val-detail-reject" data-wf-id="${wf.id}">✕ Respinge</button>
                </div>
            </div>
            <div class="val-split-right">
                ${hasPdf ? `
                    <div class="val-pdf-toolbar">
                        <span class="val-pdf-label">📄 ${escHtml(pdfDoc.filename || 'Document')}</span>
                        ${pdfSelectorHtml}
                        ${pdfExtractInfo}
                        <div class="val-pdf-nav">
                            <button class="btn btn-sm btn-outline val-pdf-prev" title="Pagina anterioară">◀</button>
                            <span class="val-pdf-page-display">p. ${pdfPageStart}</span>
                            <button class="btn btn-sm btn-outline val-pdf-next" title="Pagina următoare">▶</button>
                            <button class="btn btn-sm btn-outline val-pdf-newtab" title="Deschide în tab nou">↗</button>
                        </div>
                    </div>
                    <iframe class="val-pdf-frame" src="/api/documents/${pdfDoc.document_id}/view?page=${pdfPageStart}&embed=1"></iframe>
                ` : `
                    <div class="val-pdf-empty">
                        <span class="icon">📄</span>
                        <p>Nu există un document PDF asociat acestui interval de mentenanță.</p>
                        <p style="font-size:.85em;color:var(--text-muted)">Încărcați manualul mașinii pentru a activa comparația.</p>
                    </div>
                `}
            </div>
        </div>`;
        document.body.appendChild(detailEl);

        detailEl.querySelector('.val-detail-close')?.addEventListener('click', () => detailEl.remove());
        // se închide doar dacă verdictul chiar a plecat (la „Renunță" rămâi pe detalii)
        detailEl.querySelector('.val-detail-approve')?.addEventListener('click', async () => {
            if (await _validateWorkflow(wf.id, 'approved')) detailEl.remove();
        });
        detailEl.querySelector('.val-detail-reject')?.addEventListener('click', async () => {
            if (await _validateWorkflow(wf.id, 'rejected')) detailEl.remove();
        });
        detailEl.addEventListener('click', (e) => { if (e.target === detailEl) detailEl.remove(); });

        if (hasPdf) {
            let currentPage = pdfPageStart;
            const iframe = detailEl.querySelector('.val-pdf-frame') as HTMLIFrameElement;
            const pageDisplay = detailEl.querySelector('.val-pdf-page-display') as HTMLElement;

            const navigatePdf = (docId: number, page: number) => {
                currentPage = Math.max(1, page);
                if (iframe) iframe.src = `/api/documents/${docId}/view?page=${currentPage}&embed=1`;
                if (pageDisplay) pageDisplay.textContent = `p. ${currentPage}`;
            };

            let currentDocId = pdfDoc.document_id;

            detailEl.querySelector('.val-pdf-prev')?.addEventListener('click', () => {
                navigatePdf(currentDocId, currentPage - 1);
            });
            detailEl.querySelector('.val-pdf-next')?.addEventListener('click', () => {
                navigatePdf(currentDocId, currentPage + 1);
            });
            detailEl.querySelector('.val-pdf-newtab')?.addEventListener('click', () => {
                window.open(`/api/documents/${currentDocId}/view?page=${currentPage}`, '_blank');
            });

            const selector = detailEl.querySelector('.val-pdf-selector') as HTMLSelectElement;
            if (selector) {
                selector.addEventListener('change', () => {
                    const idx = parseInt(selector.value);
                    const d = pdfCtx.documents[idx];
                    if (d) {
                        currentDocId = d.document_id;
                        navigatePdf(d.document_id, d.page || 1);
                    }
                });
            }

            detailEl.querySelectorAll('.val-step-page-btn').forEach(btn => {
                (btn as HTMLElement).addEventListener('click', (e) => {
                    e.stopPropagation();
                    const docId = parseInt((btn as HTMLElement).dataset.doc || '0');
                    const page = parseInt((btn as HTMLElement).dataset.page || '1');
                    if (docId) {
                        currentDocId = docId;
                        navigatePdf(docId, page);
                    }
                });
            });
        }

        /* Comutatorul pași/PDF are rost DOAR când există un PDF. Fără el apărea
           oricum, scria „📋 Arată pași" cu pașii deja pe ecran, iar o apăsare
           ducea la un panou gol din care trebuia să te întorci. */
        if (window.innerWidth <= 768 && hasPdf) {
            const toggleBtn = document.createElement('button');
            toggleBtn.className = 'btn btn-sm btn-outline val-toggle-panel';
            toggleBtn.textContent = '📄 Arată PDF';
            toggleBtn.style.cssText = 'position:absolute;top:8px;right:50px;z-index:2';
            detailEl.querySelector('.val-split-container')?.appendChild(toggleBtn);
            toggleBtn.addEventListener('click', () => {
                const container = detailEl.querySelector('.val-split-container') as HTMLElement;
                if (container) {
                    container.classList.toggle('val-show-pdf');
                    toggleBtn.textContent = container.classList.contains('val-show-pdf') ? '📋 Arată pași' : '📄 Arată PDF';
                }
            });
        }
    } catch (e) { toast('Eroare la detalii', 'error'); }
}


/* ═══ VALIDATION BADGE (for sidebar) ═════════════════════════════════ */

export async function updateValidationBadge(): Promise<void> {
    try {
        const resp = await fetch('/api/pending-validations');
        const data = await resp.json();
        const badge = document.getElementById('validation-badge');
        if (badge) {
            // semnal zilnic: doar elementele din ultimele 14 zile; backlogul
            // istoric rămâne vizibil în secțiune, nu în badge (audit UX 30 iul)
            const n = (typeof data.recent_total === 'number') ? data.recent_total : data.total;
            if (n > 0) {
                badge.textContent = String(n);
                badge.title = data.total > n ? `${data.total} în total (badge = ultimele 14 zile)` : '';
                badge.style.display = 'inline-flex';
            } else {
                badge.style.display = 'none';
            }
        }
    } catch (e) {}
}
